import React from "react";
import { CornerDownLeft, Delete, X } from "lucide-react";
import { cn } from "@/lib/utils";

function KeypadBtn({ label, onClick, variant = "num", testid, spanRow, ariaLabel }) {
  const styles = {
    num: "bg-white border-[#F0E0D4] text-brand-brown hover:bg-[#FFF3E8] text-[22px] font-bold",
    amber: "bg-[#FFF3E8] border-brand-orange/40 text-brand-orange hover:bg-[#FFE8D4] text-[12px] font-bold",
    red: "bg-red-50 border-red-300 text-red-700 hover:bg-red-100 text-[12px] font-bold",
    green: "bg-brand-green border-brand-green text-white hover:bg-[#1e9a52] text-[13px] font-black",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid || `keypad-${typeof label === "string" ? label : "btn"}`}
      aria-label={ariaLabel}
      className={cn(
        "h-14 rounded-xl border transition-colors active:translate-y-px flex items-center justify-center touch-manipulation",
        styles[variant],
        spanRow && "row-span-2 h-auto min-h-[7.5rem]",
      )}
    >
      {label}
    </button>
  );
}

export default function PosKeypadPopup({
  open,
  onClose,
  modeConfig,
  buffer,
  onDigit,
  onBackspace,
  onClear,
  onApply,
  onCancelMode,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[180] flex items-end sm:items-center justify-center bg-brand-brown/15 backdrop-blur-[2px] p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Numeric keypad"
      data-testid="pos-keypad-popup"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[360px] rounded-2xl border border-[#F0E0D4] bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-3 py-2.5 flex items-start justify-between gap-2 bg-[#FFFCFA] border-b border-[#F5EBE3]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-brand-orange">Active operation</div>
            <div className="text-[15px] font-black leading-tight truncate text-brand-brown">{modeConfig.label}</div>
            <div className="text-[11px] text-brand-brown/60 leading-tight mt-0.5">{modeConfig.hint}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 shrink-0 rounded-lg bg-[#FFF3E8] hover:bg-[#FFE8D4] text-brand-brown grid place-items-center border border-[#F0E0D4]"
            aria-label="Close keypad"
          >
            <X size={16} />
          </button>
        </div>

        <div className="bg-[#FFF3E8] px-3 py-2.5 flex items-center justify-between font-mono border-b border-[#F5EBE3]" data-testid="keypad-display">
          <span className="text-[10px] uppercase font-bold text-brand-brown/45 tracking-wider">Entry</span>
          <span className="text-brand-orange font-black tabular-nums text-[26px]" data-testid="keypad-buffer">
            {buffer || "0"}
            {modeConfig.suffix && (
              <span className="text-[13px] text-brand-brown/40 ml-1.5">{modeConfig.suffix}</span>
            )}
          </span>
        </div>

        <div className="p-2.5 grid grid-cols-4 gap-2 bg-[#FAF9F6]">
          <KeypadBtn label="7" onClick={() => onDigit("7")} />
          <KeypadBtn label="8" onClick={() => onDigit("8")} />
          <KeypadBtn label="9" onClick={() => onDigit("9")} />
          <KeypadBtn label={<Delete size={18} />} onClick={onBackspace} variant="amber" testid="keypad-bs" ariaLabel="Backspace" />
          <KeypadBtn label="4" onClick={() => onDigit("4")} />
          <KeypadBtn label="5" onClick={() => onDigit("5")} />
          <KeypadBtn label="6" onClick={() => onDigit("6")} />
          <KeypadBtn label="CL" onClick={onClear} variant="red" testid="keypad-CL" />
          <KeypadBtn label="1" onClick={() => onDigit("1")} />
          <KeypadBtn label="2" onClick={() => onDigit("2")} />
          <KeypadBtn label="3" onClick={() => onDigit("3")} />
          <KeypadBtn
            label={<span className="flex items-center gap-1"><CornerDownLeft size={15} /> ENT</span>}
            onClick={onApply}
            variant="green"
            testid="keypad-ENT"
            spanRow
            ariaLabel="Apply"
          />
          <KeypadBtn label="0" onClick={() => onDigit("0")} />
          <KeypadBtn label="." onClick={() => onDigit(".")} />
          <KeypadBtn label="00" onClick={() => onDigit("00")} />
        </div>

        <div className="px-2.5 pb-2.5 bg-[#FAF9F6]">
          <button
            type="button"
            onClick={onCancelMode}
            className="w-full h-9 rounded-xl border border-[#F0E0D4] bg-white text-brand-brown/70 text-[11px] font-bold uppercase tracking-wide hover:bg-[#FFF3E8]"
            data-testid="keypad-cancel-mode"
          >
            Cancel · ESC
          </button>
        </div>
      </div>
    </div>
  );
}
